/**
 * usePwaInstall.js
 *
 * The `beforeinstallprompt` event fires once, very early in the page lifecycle —
 * often before any React component has mounted. If we listen inside a useEffect,
 * we always miss it.
 *
 * Solution: capture it at MODULE LOAD TIME (runs as soon as this file is imported,
 * synchronously before any component renders), store it in a module-level variable,
 * and expose it via a hook + setter so components can read and trigger it.
 *
 * IMPORTANT — iOS Safari:
 * `beforeinstallprompt` is a Chromium-only API. Safari on iOS/iPadOS NEVER fires it,
 * so `_deferredPrompt` stays null forever on iPhone and `canInstall` would always be
 * false. iOS has no programmatic install API — the only path is the user manually
 * tapping Share -> "Add to Home Screen". We detect that platform separately and
 * expose `canInstall` (real one-tap prompt) and `isIOSInstallable` (show manual
 * instructions) as two distinct signals so the UI can branch on either.
 */

import { useState, useEffect, useCallback } from "react";

// ── Module-level singleton ──────────────────────────────────────────────────
let _deferredPrompt = null;
let _isInstalled =
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

// Detect iOS Safari (iPhone/iPad/iPod, excluding in-app browsers pretending
// to be desktop Safari via "MacIntel" + touch points on newer iPadOS)
const _isIOS =
  typeof window !== "undefined" &&
  (/iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
    (window.navigator.platform === "MacIntel" &&
      window.navigator.maxTouchPoints > 1));

const _isSafari =
  typeof window !== "undefined" &&
  /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(
    window.navigator.userAgent
  );

// Subscribers that get notified when the prompt arrives or installed state changes
const _listeners = new Set();

const _notify = () => {
  _listeners.forEach((fn) => fn());
};

// Capture the prompt as early as possible — at import time
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    _notify();
  });

  window.addEventListener("appinstalled", () => {
    _isInstalled = true;
    _deferredPrompt = null;
    _notify();
  });
}

// ── React hook ──────────────────────────────────────────────────────────────
export function usePwaInstall() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const cb = () => forceUpdate((n) => n + 1);
    _listeners.add(cb);
    return () => _listeners.delete(cb);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!_deferredPrompt) return;
    _deferredPrompt.prompt();
    const { outcome } = await _deferredPrompt.userChoice;
    if (outcome === "accepted") {
      _deferredPrompt = null;
      _notify();
    }
  }, []);

  // iOS/Safari can never produce a _deferredPrompt, so give the UI a way to
  // know "installable, but only via manual Add to Home Screen instructions"
  const isIOSInstallable = _isIOS && _isSafari && !_isInstalled;

  return {
    canInstall: !_isInstalled && !!_deferredPrompt,
    isIOSInstallable,
    isInstalled: _isInstalled,
    handleInstall,
  };
}